import React, { useEffect, useState, useRef } from "react";
import "./Quizzes.css";
import { useNavigate, useParams } from "react-router-dom";
import { StudentMapMenuLight } from "../Map/MapMenu/MapMenuLight";
import { useGetQuizList } from "../../../../hooks/student/quiz/useGetQuizList";
import { useStoreQuizProgress } from "../../../../hooks/student/quiz/useStoreQuizProgress";
import { useAwardStudentTrophy } from "../../../../hooks/student/trophy/useAwardStudentTrophy";
import QuizSkeleton from "../../../common/QuizSkeleton";

export const SchoolStudentAdminQuizz = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState([]);
  const [quizzesMap, setQuizzesMap] = useState({ en: [], hi: [] });
  const [language, setLanguage] = useState(
    localStorage.getItem("language") || "en",
  );

  const { questId, chapterId } = useParams();
  const navigate = useNavigate();

  const { schlStudentQuiz, loading, getSchlStudentQuizList } = useGetQuizList();
  const { storeQuizProgress } = useStoreQuizProgress();
  const { awardStudentTrophy } = useAwardStudentTrophy();
  const [limitReached, setLimitReached] = useState(false);
  const [limitMessage, setLimitMessage] = useState("");

  /* 🔊 Sounds (public URLs – no AccessDenied) */
  const nextSoundRef = useRef(
    new Audio(
      "https://d2lvj32mp5x83n.cloudfront.net/static/studentadmin/quiz-sound/correct-6033.mp3",
    ),
  );

  const backSoundRef = useRef(
    new Audio(
      "https://d2lvj32mp5x83n.cloudfront.net/static/studentadmin/quiz-sound/correct-6033.mp3",
    ),
  );

  const submitSoundRef = useRef(
    new Audio(
      "https://d2lvj32mp5x83n.cloudfront.net/static/studentadmin/quiz-sound/correct-6033.mp3",
    ),
  );

  /* Transform bilingual API data for a specific language */
  const transformQuestions = (data, lang) =>
    data.map((item) => {
      const getLang = (field) =>
        field && typeof field === "object" ? field[lang] || "" : field || "";

      const options = [
        getLang(item.option1),
        getLang(item.option2),
        getLang(item.option3),
        getLang(item.option4),
      ];

      const correctAnswer = getLang(item.correctAnswer);

      return {
        id: item.id,
        question: getLang(item.question),
        options,
        images: {
          question:
            item.images?.question?.[lang] || item.images?.question?.en || "",
        },
        correctIndex: options.indexOf(correctAnswer),
      };
    });

  // Derive the active quiz list from the cached map
  const quizes = quizzesMap[language] || [];

  /* Fetch quiz list once */
  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        const response = await getSchlStudentQuizList();
        const data = response?.data;

        if (data?.data?.type) {
          localStorage.setItem("quiz_type", data.data.type);
        }

        if (
          data?.success === false &&
          data?.error_code === "RESOURCE_CONFLICT" &&
          data?.message === "You reached the max attempts for this quiz."
        ) {
          setLimitReached(true);
          setLimitMessage(data.message);
          setQuizzesMap({ en: [], hi: [] });
          return;
        }

        setLimitReached(false);
      } catch (err) {
        console.error("Quiz fetch error", err);
      }
    };

    fetchQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Format API response into bilingual map */
  useEffect(() => {
    if (limitReached) {
      setQuizzesMap({ en: [], hi: [] });
      return;
    }

    if (Array.isArray(schlStudentQuiz) && schlStudentQuiz.length > 0) {
      setQuizzesMap({
        en: transformQuestions(schlStudentQuiz, "en"),
        hi: transformQuestions(schlStudentQuiz, "hi"),
      });
      setCurrentQuestionIndex(0);
      setSelectedOptionIndex([]);
    }
  }, [schlStudentQuiz, limitReached]);

  /* Option select */
  const handleOptionChange = (index) => {
    const updated = [...selectedOptionIndex];
    updated[currentQuestionIndex] = index;
    setSelectedOptionIndex(updated);
  };

  /* Back */
  const handlePrevious = () => {
    backSoundRef.current.currentTime = 0;
    backSoundRef.current.play();
    setCurrentQuestionIndex((prev) => prev - 1);
  };

  /* Next / Submit */
  const handleNext = () => {
    if (currentQuestionIndex === quizes.length - 1) {
      submitSoundRef.current.currentTime = 0;
      submitSoundRef.current.play();
      calculateScore();
    } else {
      nextSoundRef.current.currentTime = 0;
      nextSoundRef.current.play();
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  /* Store progress */
  const storeProgress = async ({ answers }) => {
    try {
      const response = await storeQuizProgress({
        quest_chapter_id: Number(chapterId),
        answers,
      });

      //  SESSION STORAGE ME SAVE
      sessionStorage.setItem(
        "quizProgressApiResponse",
        JSON.stringify(response.data),
      );

      //  VERIFY
      console.log(
        "SESSION STORED API DATA ",
        JSON.parse(sessionStorage.getItem("quizProgressApiResponse")),
      );

      return response;
    } catch (error) {
      console.error("Error storing quiz progress", error);
    }
  };

  /* Final score */
  const calculateScore = async () => {
    const answers = [];

    quizes.forEach((quiz, index) => {
      const selectedIdx = selectedOptionIndex[index];

      if (selectedIdx !== undefined) {
        answers.push({
          id: quiz.id,
          selected: quiz.options[selectedIdx],
        });
      }
    });

    //  WAIT FOR API & SESSION STORE
    const apiResponse = await storeProgress({ answers });

    const position = localStorage.getItem("chapter");

    if (position === "end") {
      await awardStudentTrophy({ quest_id: Number(questId) });
      navigate(`/student/admin/congratulation/trophy/${chapterId}/${questId}`);
    } else {
      navigate(`/student/admin/congratulation/${chapterId}/${questId}`);
    }
  };

  /* Language */
  const handleChange = (e) => {
    setLanguage(e.target.value);
    localStorage.setItem("language", e.target.value);
  };

  return (
    <>
      {!limitReached ? (
        <div
          className="StudentAdminBody"
          style={{ backgroundColor: "#0037ac", minHeight: "100vh" }}
        >
          <div className="container position-relative" style={{ zIndex: 3 }}>
            <div className="container col-md-3 pt-4">
              <select
                className="form-select"
                value={language}
                onChange={handleChange}
              >
                <option value="en">English</option>
                <option value="hi">Hindi</option>
              </select>
            </div>

            <div className="row justify-content-center">
              <div className="col-12 col-md-8">
                <div className="quiz-container p-4">
                  {loading ? (
                    <QuizSkeleton />
                  ) : (
                    <>
                      {/* Progress */}
                      <div className="d-flex justify-content-between mb-4 mt-4">
                        {quizes.map((_, index) => (
                          <span
                            key={index}
                            className={`progress-line ${
                              index <= currentQuestionIndex ? "active" : ""
                            }`}
                          />
                        ))}
                      </div>

                      {/* Question */}
                      <h5 className="text-center questionNoText">
                        Question {currentQuestionIndex + 1}/{quizes.length}
                      </h5>
                      {quizes[currentQuestionIndex]?.images?.question && (
                        <div className="QuizImageCard">
                          <img
                            src={quizes[currentQuestionIndex].images.question}
                            alt="question"
                          />
                        </div>
                      )}

                      <p className="text-center my-4 questionText">
                        {quizes[currentQuestionIndex]?.question}
                      </p>
                      {/* Options */}
                      <div className="options-container">
                        {quizes[currentQuestionIndex]?.options?.map(
                          (option, index) => {
                            const isSelected =
                              selectedOptionIndex[currentQuestionIndex] ===
                              index;

                            return (
                              <div
                                key={index}
                                className="option d-flex align-items-center"
                                onClick={() => handleOptionChange(index)}
                                style={{
                                  backgroundColor: isSelected ? "#007BFF" : "",
                                  color: isSelected ? "white" : "black",
                                  cursor: "pointer",
                                }}
                              >
                                <span className="option-label">
                                  {String.fromCharCode(65 + index)}
                                </span>
                                <span className="option-text">{option}</span>
                              </div>
                            );
                          },
                        )}
                      </div>
                      {/* Buttons */}
                      <div className="d-flex justify-content-between mt-4">
                        {currentQuestionIndex > 0 && (
                          <button
                            className="btn quizChange"
                            onClick={handlePrevious}
                          >
                            {language === "hi" ? "पीछे" : "Back"}
                          </button>
                        )}

                        <button
                          className="btn quizChange"
                          onClick={handleNext}
                          disabled={
                            selectedOptionIndex[currentQuestionIndex] ===
                            undefined
                          }
                        >
                          {currentQuestionIndex === quizes.length - 1
                            ? language === "hi"
                              ? "सबमिट करें"
                              : "Submit"
                            : language === "hi"
                              ? "अगला"
                              : "Next"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="StudentAdminBody d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "#0037ac", minHeight: "100vh" }}
        >
          <div className="text-center bg-white px-4 py-5">
            <div className="px-4">
              <h2>Attempts Limit Reached</h2>
              <img
                src="https://d2lvj32mp5x83n.cloudfront.net/static/studentadmin/quiz/Vector+(1).png"
                alt=""
              />
              <p className="mt-3">
                You’ve reached the maximum number of attempts for this quiz.
              </p>
              <p>Please contact your teacher if you need further assistance.</p>

              <button
                className="btn quizChange mt-4 px-5 border-0"
                onClick={() => navigate("/student/start-your-quest")}
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ position: "relative", zIndex: 4 }}>
        <StudentMapMenuLight />
      </div>
    </>
  );
};
